import { COLORS } from "../../shared/constants";
import { useState, useRef } from "react";
import * as api from "../../api";

export default function ImportExportTab() {
  // Import state
  const [importing, setImporting] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [message, setMessage] = useState(null);
  const sessionFileRef = useRef(null);
  const assessmentFileRef = useRef(null);

  // Export state
  const [exporting, setExporting] = useState(null);
  const [exportFilters, setExportFilters] = useState({
    patientIdFrom: "",
    patientIdTo: "",
    dateFrom: "",
    dateTo: "",
    deIdentified: false,
  });

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Import handlers ──
  const handleImportSessions = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting("sessions");
    setImportResult(null);
    setMessage(null);
    try {
      const result = await api.importSessions(file);
      setImportResult({ type: "sessions", ...result });
      const dupNote = result.duplicates_skipped > 0 ? ` (${result.duplicates_skipped} duplicates skipped)` : "";
      setMessage({ type: "success", text: `Imported ${result.sessions_created} sessions successfully.${dupNote}` });
    } catch (e) {
      const data = e || {};
      setImportResult({ type: "sessions", success: false, ...data });
      setMessage({ type: "error", text: data.errors?.join("; ") || data.error || "Session import failed." });
    }
    setImporting(null);
    if (sessionFileRef.current) sessionFileRef.current.value = "";
  };

  const handleImportAssessments = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting("assessments");
    setImportResult(null);
    setMessage(null);
    try {
      const result = await api.importAssessments(file);
      setImportResult({ type: "assessments", ...result });
      const dupNote = result.duplicates_skipped > 0 ? ` (${result.duplicates_skipped} duplicates skipped)` : "";
      setMessage({ type: "success", text: `Imported ${result.assessments_created} assessments successfully.${dupNote}` });
    } catch (e) {
      const data = e || {};
      setImportResult({ type: "assessments", success: false, ...data });
      setMessage({ type: "error", text: data.errors?.join("; ") || data.error || "Assessment import failed." });
    }
    setImporting(null);
    if (assessmentFileRef.current) assessmentFileRef.current.value = "";
  };

  const handleDownloadTemplate = async (type) => {
    setMessage(null);
    try {
      const blob = type === "sessions"
        ? await api.downloadSessionTemplate()
        : await api.downloadAssessmentTemplate();
      const filename = type === "sessions"
        ? "TMS_Sessions_Import_Template.csv"
        : "TMS_Assessments_Import_Template.csv";
      downloadBlob(blob, filename);
    } catch (e) {
      setMessage({ type: "error", text: "Failed to download template." });
    }
  };

  // ── Export handlers ──
  const buildExportParams = () => {
    const params = {};
    if (exportFilters.dateFrom) params.date_from = exportFilters.dateFrom;
    if (exportFilters.dateTo) params.date_to = exportFilters.dateTo;
    if (exportFilters.deIdentified) params.de_identified = "true";
    // Patient ID range handled client-side via patient_id_from/to query params
    if (exportFilters.patientIdFrom) params.patient_id_from = exportFilters.patientIdFrom;
    if (exportFilters.patientIdTo) params.patient_id_to = exportFilters.patientIdTo;
    return params;
  };

  const handleExportSessions = async () => {
    setExporting("sessions");
    setMessage(null);
    try {
      const params = buildExportParams();
      const blob = await api.exportSessions(params);
      const prefix = exportFilters.deIdentified ? "DeIdentified_" : "";
      const filename = `TMS_Sessions_${prefix}Export_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.csv`;
      downloadBlob(blob, filename);
      setMessage({ type: "success", text: "Session data exported." });
    } catch (e) {
      setMessage({ type: "error", text: e.error || "Export failed." });
    }
    setExporting(null);
  };

  const handleExportAssessments = async () => {
    setExporting("assessments");
    setMessage(null);
    try {
      const params = buildExportParams();
      const blob = await api.exportAssessments(params);
      const prefix = exportFilters.deIdentified ? "DeIdentified_" : "";
      const filename = `TMS_Assessments_${prefix}Export_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.csv`;
      downloadBlob(blob, filename);
      setMessage({ type: "success", text: "Assessment data exported." });
    } catch (e) {
      setMessage({ type: "error", text: e.error || "Export failed." });
    }
    setExporting(null);
  };

  const busy = !!importing || !!exporting;

  const btnStyle = (variant = "default") => ({
    padding: "10px 18px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "500",
    cursor: busy ? "wait" : "pointer",
    opacity: busy ? 0.7 : 1,
    display: "flex",
    alignItems: "center",
    gap: "6px",
    border: `1px solid ${variant === "accent" ? "#059669" : variant === "primary" ? COLORS.primary : COLORS.border}`,
    background: variant === "primary" ? COLORS.primary : COLORS.surface,
    color: variant === "accent" ? "#059669" : variant === "primary" ? "#fff" : COLORS.text,
  });

  const inputStyle = {
    height: "36px", padding: "0 10px", borderRadius: "6px",
    border: `1px solid ${COLORS.border}`, fontSize: "13px", color: COLORS.text,
    background: COLORS.surface, boxSizing: "border-box",
  };

  return (
    <div>
      {/* Status Message */}
      {message && (
        <div style={{
          padding: "10px 14px", borderRadius: "8px", marginBottom: "16px", fontSize: "13px",
          background: message.type === "success" ? "#E8F8F0" : COLORS.dangerLight,
          color: message.type === "success" ? "#2E9E6A" : COLORS.danger,
          border: `1px solid ${message.type === "success" ? "#A7E8C5" : COLORS.dangerBorder}`,
        }}>
          {message.text}
        </div>
      )}

      {/* ═══════════════════ EXPORT ═══════════════════ */}
      <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, marginBottom: "6px" }}>
        Data Export
      </h3>
      <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "16px", lineHeight: "1.5" }}>
        Export all session and assessment data as CSV. Optionally filter by patient ID range and date range.
      </p>

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "14px", alignItems: "flex-end" }}>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>Patient ID From</label>
          <input type="number" style={{ ...inputStyle, width: "110px" }} placeholder="e.g. 1001"
            value={exportFilters.patientIdFrom} onChange={(e) => setExportFilters(f => ({ ...f, patientIdFrom: e.target.value }))} />
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>Patient ID To</label>
          <input type="number" style={{ ...inputStyle, width: "110px" }} placeholder="e.g. 1050"
            value={exportFilters.patientIdTo} onChange={(e) => setExportFilters(f => ({ ...f, patientIdTo: e.target.value }))} />
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>Date From</label>
          <input type="date" style={{ ...inputStyle, width: "150px" }}
            value={exportFilters.dateFrom} onChange={(e) => setExportFilters(f => ({ ...f, dateFrom: e.target.value }))} />
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>Date To</label>
          <input type="date" style={{ ...inputStyle, width: "150px" }}
            value={exportFilters.dateTo} onChange={(e) => setExportFilters(f => ({ ...f, dateTo: e.target.value }))} />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: COLORS.textSecondary, cursor: "pointer", paddingBottom: "8px" }}>
          <input type="checkbox" checked={exportFilters.deIdentified} onChange={(e) => setExportFilters(f => ({ ...f, deIdentified: e.target.checked }))} style={{ width: "16px", height: "16px" }} />
          De-identified
        </label>
      </div>

      {/* Export Buttons */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "32px" }}>
        <button onClick={handleExportSessions} disabled={busy} style={btnStyle("primary")}>
          📊 {exporting === "sessions" ? "Exporting..." : "Export Sessions CSV"}
        </button>
        <button onClick={handleExportAssessments} disabled={busy} style={btnStyle("primary")}>
          📊 {exporting === "assessments" ? "Exporting..." : "Export Assessments CSV"}
        </button>
      </div>

      {/* ═══════════════════ IMPORT ═══════════════════ */}
      <div style={{ borderTop: `1px solid ${COLORS.borderLight}`, paddingTop: "24px" }}>
        <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, marginBottom: "6px" }}>
          Data Import
        </h3>
        <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "20px", lineHeight: "1.5" }}>
          Upload historical data from Patient 360 or other sources. Protocols and assessment scales must already exist in the EHR. Import sessions first, then assessments.
        </p>

        {/* Sessions Import */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: COLORS.text, marginBottom: "8px" }}>
            1. Import Sessions
          </div>
          <p style={{ fontSize: "12px", color: COLORS.textSecondary, marginBottom: "10px", lineHeight: "1.5" }}>
            Each row is one treatment session. Columns must include: first_name, last_name, date_of_birth, session_number, session_date.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <button onClick={() => sessionFileRef.current?.click()} disabled={busy} style={btnStyle("accent")}>
              📥 {importing === "sessions" ? "Importing..." : "Upload Sessions CSV"}
            </button>
            <input ref={sessionFileRef} type="file" accept=".csv,.txt" onChange={handleImportSessions}
              style={{ display: "none" }} />
            <button onClick={() => handleDownloadTemplate("sessions")}
              style={{ background: "none", border: "none", color: COLORS.primary, fontSize: "12px", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
              ↓ Download sample template
            </button>
          </div>
        </div>

        {/* Assessments Import */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: COLORS.text, marginBottom: "8px" }}>
            2. Import Assessments
          </div>
          <p style={{ fontSize: "12px", color: COLORS.textSecondary, marginBottom: "10px", lineHeight: "1.5" }}>
            Each row is one assessment score. Columns must include: first_name, last_name, date_of_birth, scale_short_name, total_score.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <button onClick={() => assessmentFileRef.current?.click()} disabled={busy} style={btnStyle("accent")}>
              📥 {importing === "assessments" ? "Importing..." : "Upload Assessments CSV"}
            </button>
            <input ref={assessmentFileRef} type="file" accept=".csv,.txt" onChange={handleImportAssessments}
              style={{ display: "none" }} />
            <button onClick={() => handleDownloadTemplate("assessments")}
              style={{ background: "none", border: "none", color: COLORS.primary, fontSize: "12px", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
              ↓ Download sample template
            </button>
          </div>
        </div>

        {/* Import Result Detail */}
        {importResult && importResult.success && (
          <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "8px", padding: "14px 16px", fontSize: "13px", color: "#166534", lineHeight: "1.6" }}>
            <strong>Import complete</strong>
            {importResult.type === "sessions" && (
              <span>
                {" "}— {importResult.sessions_created} sessions, {importResult.patients_created} new patients, {importResult.treatments_created} new treatments
                {importResult.duplicates_skipped > 0 && `, ${importResult.duplicates_skipped} duplicates skipped`}
                {importResult.sessions_skipped > 0 && `, ${importResult.sessions_skipped} skipped`}
              </span>
            )}
            {importResult.type === "assessments" && (
              <span>
                {" "}— {importResult.assessments_created} assessments
                {importResult.patients_created > 0 && `, ${importResult.patients_created} new patients`}
                {importResult.duplicates_skipped > 0 && `, ${importResult.duplicates_skipped} duplicates skipped`}
                {importResult.assessments_skipped > 0 && `, ${importResult.assessments_skipped} skipped`}
              </span>
            )}
            {importResult.warnings && importResult.warnings.length > 0 && (
              <div style={{ marginTop: "10px", color: "#92400E", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: "6px", padding: "10px 12px" }}>
                <strong>Warnings ({importResult.warnings.length}):</strong>
                <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                  {importResult.warnings.slice(0, 10).map((w, i) => <li key={i} style={{ marginBottom: "2px" }}>{w}</li>)}
                  {importResult.warnings.length > 10 && <li>...and {importResult.warnings.length - 10} more</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        {importResult && !importResult.success && importResult.errors && importResult.errors.length > 0 && (
          <div style={{ background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: "8px", padding: "14px 16px", fontSize: "13px", color: COLORS.danger, lineHeight: "1.6" }}>
            <strong>Import failed:</strong>
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
